/**
 * DynamicRenderer - Generative UI Engine
 *
 * Interprets JSON schemas from the AI and renders them as React components.
 * The AI composes primitives on the fly to create custom UIs for any query.
 *
 * Schema format:
 * {
 *   type: 'column' | 'row' | 'card' | 'table' | etc.,
 *   props: { ... component-specific props },
 *   children: [ ... nested schemas ]
 * }
 */

import React from 'react';
import { Column, Row, Grid, Card, Accordion, Section } from './LayoutPrimitives';
import { Text, Heading, Divider, Badge, Alert, Code } from './ContentPrimitives';
import { Table, List, Stat, StatRow, Progress, MiniChart } from './DataPrimitives';
import { Button, Link, Chip, ChipGroup } from './InteractivePrimitives';
import {
  MiniSankey,
  MiniTrajectory,
  MiniHeatmap,
  MiniRadar,
  MiniBarChart,
  SparkLine,
} from './VisualizationPrimitives';
import SuggestionBar, { SuggestionChip, QuickActions } from './SuggestionBar';
import InteractiveTaskCard, { InteractiveTaskList, TaskStats } from './InteractiveTaskCard';
import TaskKanban, { TaskKanbanModal } from './TaskKanban';
import DayDashboard, { MiniDayDashboard } from './DayDashboard';
import CalendarWidget, { MiniCalendar, CalendarEventCard } from './CalendarWidget';
import MermaidDiagram, { NetworkDiagram, ArchitectureDiagram } from './MermaidDiagram';
import DrillableValue from './DrillableValue';
import DealWaterfall from './DealWaterfall';

// Logging prefix for easy filtering
const LOG_PREFIX = '[DynamicRenderer]';

// Component registry - maps type names to React components
const PRIMITIVES = {
  // Layout
  column: Column,
  row: Row,
  grid: Grid,
  card: Card,
  accordion: Accordion,
  section: Section,

  // Content
  text: Text,
  heading: Heading,
  divider: Divider,
  badge: Badge,
  alert: Alert,
  code: Code,

  // Data
  table: Table,
  list: List,
  stat: Stat,
  'stat-row': StatRow,
  progress: Progress,
  chart: MiniChart,

  // Interactive
  button: Button,
  link: Link,
  chip: Chip,
  'chip-group': ChipGroup,

  // Visualizations
  'mini-sankey': MiniSankey,
  'mini-trajectory': MiniTrajectory,
  'mini-heatmap': MiniHeatmap,
  'mini-radar': MiniRadar,
  'mini-bar': MiniBarChart,
  sparkline: SparkLine,

  // Suggestions (Phase 3)
  'suggestion-bar': SuggestionBar,
  'suggestion-chip': SuggestionChip,
  'quick-actions': QuickActions,

  // Interactive Tasks (Wave 4)
  'interactive-task': InteractiveTaskCard,
  'task-list': InteractiveTaskList,
  'task-stats': TaskStats,
  'task-kanban': TaskKanban,
  'task-kanban-modal': TaskKanbanModal,

  // Unified Dashboard (Wave 5)
  'day-dashboard': DayDashboard,
  'mini-day-dashboard': MiniDayDashboard,

  // Calendar Integration (Wave 6)
  'calendar': CalendarWidget,
  'mini-calendar': MiniCalendar,
  'calendar-event': CalendarEventCard,

  // Diagrams (Wave 6.5)
  'mermaid-diagram': MermaidDiagram,
  'network-diagram': NetworkDiagram,
  'architecture-diagram': ArchitectureDiagram,

  // Provenance (Wave 7) — wrap any value to make it open the transaction
  // drill-down modal: { type: 'drillable', drill: { nodeType, nodeValue, ... }, children: '$13.7M' }
  'drillable': DrillableValue,

  // Deal construction (Wave 14) — interactive financed-deal pricing with
  // client-side discount/tier sliders
  'deal-waterfall': DealWaterfall,
};

// Track render stats for logging
let renderStats = {
  totalRenders: 0,
  componentCounts: {},
  errors: [],
  startTime: null,
};

/**
 * Reset render stats (call at start of new render)
 */
function resetStats() {
  renderStats = {
    totalRenders: 0,
    componentCounts: {},
    errors: [],
    startTime: Date.now(),
  };
}

/**
 * Log render stats summary
 */
function logStats() {
  const duration = Date.now() - renderStats.startTime;
  console.log(`${LOG_PREFIX} Render complete`, {
    totalComponents: renderStats.totalRenders,
    componentCounts: renderStats.componentCounts,
    errors: renderStats.errors.length,
    durationMs: duration,
  });
  if (renderStats.errors.length > 0) {
    console.warn(`${LOG_PREFIX} Render errors:`, renderStats.errors);
  }
}

/**
 * Recursively render a UI schema
 */
function DynamicRenderer({ schema, onAction, depth = 0, _isRoot = true }) {
  // Initialize stats on root render
  if (_isRoot && depth === 0) {
    resetStats();
    console.log(`${LOG_PREFIX} Starting render`, {
      schemaType: typeof schema,
      isArray: Array.isArray(schema),
      hasSchema: !!schema,
    });
  }

  // Safety: prevent infinite recursion
  if (depth > 20) {
    const error = `Max depth exceeded at depth ${depth}`;
    console.error(`${LOG_PREFIX} ${error}`);
    renderStats.errors.push({ type: 'max_depth', depth });
    return null;
  }

  // Handle null/undefined
  if (!schema) {
    if (depth === 0) {
      console.warn(`${LOG_PREFIX} Empty schema provided`);
    }
    return null;
  }

  // Handle arrays of schemas
  if (Array.isArray(schema)) {
    console.log(`${LOG_PREFIX} Rendering array`, {
      depth,
      itemCount: schema.length,
    });

    const result = (
      <>
        {schema.map((item, idx) => (
          <DynamicRenderer
            key={idx}
            schema={item}
            onAction={onAction}
            depth={depth + 1}
            _isRoot={false}
          />
        ))}
      </>
    );

    // Log stats on root completion
    if (_isRoot && depth === 0) {
      logStats();
    }

    return result;
  }

  // Handle string content (raw text)
  if (typeof schema === 'string') {
    renderStats.totalRenders++;
    renderStats.componentCounts['text'] = (renderStats.componentCounts['text'] || 0) + 1;
    return <Text>{schema}</Text>;
  }

  // Handle number content
  if (typeof schema === 'number') {
    renderStats.totalRenders++;
    renderStats.componentCounts['text'] = (renderStats.componentCounts['text'] || 0) + 1;
    return <Text>{schema}</Text>;
  }

  // Get the component type
  const { type, children, ...props } = schema;

  if (!type) {
    const error = `Schema missing type at depth ${depth}`;
    console.warn(`${LOG_PREFIX} ${error}`, { schema: JSON.stringify(schema).slice(0, 100) });
    renderStats.errors.push({ type: 'missing_type', depth, schema: typeof schema });
    return null;
  }

  const normalizedType = type.toLowerCase();

  // Look up the component
  const Component = PRIMITIVES[normalizedType];

  if (!Component) {
    const error = `Unknown primitive type: ${type}`;
    console.warn(`${LOG_PREFIX} ${error}`, { depth, availableTypes: Object.keys(PRIMITIVES) });
    renderStats.errors.push({ type: 'unknown_type', primitiveType: type, depth });

    // Fallback: try to render as text if there's content
    if (props.text || props.content || props.value) {
      console.log(`${LOG_PREFIX} Falling back to text for unknown type`, { type });
      return <Text>{props.text || props.content || props.value}</Text>;
    }
    return null;
  }

  // Track stats
  renderStats.totalRenders++;
  renderStats.componentCounts[normalizedType] = (renderStats.componentCounts[normalizedType] || 0) + 1;

  // Log component render at debug level (only for depth 0-2 to reduce noise)
  if (depth <= 2) {
    console.log(`${LOG_PREFIX} Rendering`, {
      type: normalizedType,
      depth,
      hasChildren: !!children,
      propKeys: Object.keys(props),
    });
  }

  // Render the component with its children
  const result = (
    <Component {...props} onAction={onAction}>
      {children && (
        <DynamicRenderer
          schema={children}
          onAction={onAction}
          depth={depth + 1}
          _isRoot={false}
        />
      )}
    </Component>
  );

  // Log stats on root completion
  if (_isRoot && depth === 0) {
    logStats();
  }

  return result;
}

/**
 * Validate a UI schema (for debugging)
 */
export function validateSchema(schema, path = 'root') {
  const errors = [];

  if (!schema) {
    errors.push(`${path}: Schema is null/undefined`);
    return errors;
  }

  if (Array.isArray(schema)) {
    schema.forEach((item, idx) => {
      errors.push(...validateSchema(item, `${path}[${idx}]`));
    });
    return errors;
  }

  if (typeof schema !== 'object') {
    return errors; // primitives are ok
  }

  if (!schema.type) {
    errors.push(`${path}: Missing 'type' property`);
  } else if (!PRIMITIVES[schema.type.toLowerCase()]) {
    errors.push(`${path}: Unknown type '${schema.type}'`);
  }

  if (schema.children) {
    errors.push(...validateSchema(schema.children, `${path}.children`));
  }

  return errors;
}

/**
 * Get list of available primitive types
 */
export function getAvailablePrimitives() {
  return Object.keys(PRIMITIVES);
}

export default DynamicRenderer;
